// System Health — operational dashboard.
//
// Two audiences served by the same page:
//   • Super-admin (Identium): full picture — worker heartbeats, backup
//     scheduler, disk free, process RAM, AND per-tenant DB sizes for
//     every tenant on the platform.
//   • Reseller admin: scoped picture — DB size + last write for THEIR
//     children only. Global infra tiles are hidden because a reseller
//     can't fix workers / disk anyway, and the data isn't theirs to see.
//
// The backend returns `scope: 'global'` or `'reseller'` and omits the
// global keys (workers/backup/disk/process) for the reseller scope; the
// page just renders what's present.
//
// Auto-refreshes every 30s. The endpoint has no side effects so polling
// is cheap; if it ever does become expensive, switch to WebSocket push.

import React, { useEffect, useState } from 'react';
import {
  MdMonitorHeart, MdRefresh, MdCheckCircle, MdError, MdWarning,
  MdStorage, MdMemory, MdBackup,
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const REFRESH_MS = 30_000;

export default function SystemHealth() {
  const { user } = useAuth();
  const isSuperAdmin =
    user?.role === 'admin' && !!user?.tenant?.is_platform_owner;
  const isResellerAdmin =
    user?.role === 'admin' && !!user?.tenant?.is_reseller && !user?.tenant?.is_platform_owner;
  const canAccessHealth = isSuperAdmin || isResellerAdmin;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastFetchAt, setLastFetchAt] = useState(null);

  const reload = async () => {
    setError('');
    try {
      const r = await api.get('/system/health');
      setData(r.data);
      setLastFetchAt(new Date());
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load health data');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!canAccessHealth) { setLoading(false); return; }
    reload();
    const t = setInterval(reload, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [canAccessHealth]);

  if (!canAccessHealth) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div className="page-title"><MdMonitorHeart /> System Health</div>
          <div className="page-subtitle">Super-admin or reseller access only</div>
        </div>
        <div className="card" style={{ padding: 24, color: 'var(--text-muted)' }}>
          Health monitoring is reserved for the platform operator and reseller-tenant admins.
        </div>
      </div>
    );
  }

  // Reseller scope: backend omits workers/backup/disk/process. Detect from
  // the `scope` field (or from a missing 'workers' key as belt-and-braces).
  const isScoped = data?.scope === 'reseller' || (data && !data.workers);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title">
            <MdMonitorHeart /> {isScoped ? 'Companies Health' : 'System Health'}
          </div>
          <div className="page-subtitle">
            {isScoped
              ? 'DB size and last-write time for the companies you onboard. Auto-refreshes every 30 seconds.'
              : 'Live status of background workers, backups, and disk usage. Auto-refreshes every 30 seconds.'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {lastFetchAt && (
            <span style={{ fontSize: 12, color: '#78909c' }}>
              updated {lastFetchAt.toLocaleTimeString()}
            </span>
          )}
          <button onClick={reload} className="btn btn-outline btn-sm"><MdRefresh /> Refresh</button>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      {loading && !data ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: '#888' }}>
          Loading health data…
        </div>
      ) : data && (
        <>
          {/* ── Global infra tiles — super-admin only ─────────────────── */}
          {!isScoped && data.workers && (
            <div style={{
              display: 'grid', gap: 12, marginBottom: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            }}>
              <WorkerTile name="Audit scheduler"  beat={data.workers.auditScheduler} />
              <WorkerTile name="Report scheduler" beat={data.workers.reportScheduler} />
              <WorkerTile name="Reader health"    beat={data.workers.readerHealth} />
              <WorkerTile name="Backup worker"    beat={data.workers.backupWorker} />
            </div>
          )}

          {!isScoped && (
            <div style={{
              display: 'grid', gap: 12, marginBottom: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}>
              <BackupTile backup={data.backup} />
              <DiskTile disk={data.disk} totalDbBytes={data.totals.dbBytes} />
              <ProcessTile proc={data.process} />
            </div>
          )}

          {/* ── Per-tenant table — both scopes ─────────────────────────── */}
          <div className="card">
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-color, #e0e6ef)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>
                {isScoped ? 'Your companies' : 'Tenants'}
                {' '}({data.totals.activeTenants}/{data.totals.tenants} active · {fmtBytes(data.totals.dbBytes)} total)
              </h3>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Slug</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>DB size</th>
                    <th>Last write (mtime)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tenants.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                        {isScoped
                          ? 'You haven\'t onboarded any companies yet. Use the Companies page to add your first one.'
                          : 'No tenants on this platform yet.'}
                      </td>
                    </tr>
                  ) : data.tenants.map(t => (
                    <tr key={t.id}>
                      <td>
                        <code style={{ fontSize: 12, background: '#eef1f6', padding: '2px 6px', borderRadius: 4 }}>{t.slug}</code>
                        {!!t.is_platform_owner && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff', background: '#6a1b9a', padding: '1px 6px', borderRadius: 4 }}>
                            YOU
                          </span>
                        )}
                        {!!t.is_reseller && !t.is_platform_owner && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff', background: '#00838f', padding: '1px 6px', borderRadius: 4 }}>
                            RESELLER
                          </span>
                        )}
                      </td>
                      <td style={{ fontWeight: 600 }}>{t.name}</td>
                      <td>
                        <span className={`badge ${t.status === 'active' ? 'badge-success' : t.status === 'suspended' ? 'badge-warning' : 'badge-secondary'}`}>
                          {t.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {t.ok ? fmtBytes(t.dbBytes) : <span style={{ color: '#c62828' }}>{t.error}</span>}
                      </td>
                      <td style={{ fontSize: 12, color: '#666' }}>
                        {t.dbMtime ? <RelativeTime iso={t.dbMtime} /> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tiles ──────────────────────────────────────────────────────────────────

function WorkerTile({ name, beat }) {
  const healthy = !!beat?.healthy;
  const has = !!beat?.lastBeatAt;
  const colour = healthy ? '#2e7d32' : has ? '#c62828' : '#90a4ae';
  const Icon   = healthy ? MdCheckCircle : has ? MdError : MdWarning;
  const label  = !has ? 'never' : healthy ? 'healthy' : 'stalled';
  return (
    <div className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
      <Icon style={{ fontSize: 28, color: colour, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#78909c', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>{name}</div>
        <div style={{ fontWeight: 700, color: colour, fontSize: 14 }}>{label}</div>
        {has && (
          <div style={{ fontSize: 11, color: '#78909c' }}>
            last beat <RelativeTime iso={beat.lastBeatAt} /> · {beat.count} ticks
          </div>
        )}
      </div>
    </div>
  );
}

function BackupTile({ backup }) {
  const last = backup?.lastSummary;
  const ok   = last && last.failed === 0;
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <MdBackup style={{ color: '#1a3c6e', fontSize: 22 }} />
        <strong>Automated backups</strong>
      </div>
      <div style={{ fontSize: 13, color: '#37474f', lineHeight: 1.6 }}>
        <div>Schedule: <strong>{backup?.scheduleLocal || '—'}</strong> daily (local)</div>
        <div>Retention: <strong>{backup?.retentionDays ?? '—'} days</strong></div>
        <div>
          Last run:{' '}
          {backup?.lastRunAt
            ? <span style={{ color: ok ? '#2e7d32' : '#c62828' }}>
                <RelativeTime iso={backup.lastRunAt} /> ({last.ok}/{last.total} ok)
              </span>
            : <span style={{ color: '#90a4ae' }}>not yet — first run scheduled</span>}
        </div>
      </div>
    </div>
  );
}

function DiskTile({ disk, totalDbBytes }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <MdStorage style={{ color: '#1a3c6e', fontSize: 22 }} />
        <strong>Disk &amp; storage</strong>
      </div>
      <div style={{ fontSize: 13, color: '#37474f', lineHeight: 1.6 }}>
        <div>Total tenant DBs: <strong>{fmtBytes(totalDbBytes)}</strong></div>
        {disk ? (
          <>
            <div>Volume free: <strong>{fmtBytes(disk.freeBytes)}</strong> of {fmtBytes(disk.totalBytes)}</div>
            <DiskBar used={disk.usedBytes} total={disk.totalBytes} />
          </>
        ) : (
          <div style={{ color: '#90a4ae', fontSize: 12 }}>(disk free unavailable on this Node version)</div>
        )}
      </div>
    </div>
  );
}

function ProcessTile({ proc }) {
  const heapPct = proc.heapTotalBytes ? Math.round(proc.heapUsedBytes / proc.heapTotalBytes * 100) : 0;
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <MdMemory style={{ color: '#1a3c6e', fontSize: 22 }} />
        <strong>Process</strong>
      </div>
      <div style={{ fontSize: 13, color: '#37474f', lineHeight: 1.6 }}>
        <div>Uptime: <strong>{fmtDuration(proc.uptimeSec)}</strong></div>
        <div>RSS: <strong>{fmtBytes(proc.rssBytes)}</strong> · heap {heapPct}%</div>
        <div>Node {proc.nodeVersion} · {proc.platform} · pid {proc.pid}</div>
        {proc.loadAvg && proc.platform !== 'win32' && (
          <div style={{ fontSize: 12, color: '#666' }}>
            load avg: {proc.loadAvg.map(n => n.toFixed(2)).join(' / ')}
          </div>
        )}
      </div>
    </div>
  );
}

function DiskBar({ used, total }) {
  const pct = total ? Math.min(100, Math.round(used / total * 100)) : 0;
  const colour = pct < 70 ? '#2e7d32' : pct < 90 ? '#f57c00' : '#c62828';
  return (
    <div style={{ marginTop: 6, background: '#eef2f8', borderRadius: 6, height: 8, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: colour, transition: 'width 0.4s' }} />
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDuration(sec) {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}

// "5 min ago", "2 days ago" etc. Updates on each render — the parent
// auto-refreshes every 30s so these stay roughly accurate.
function RelativeTime({ iso }) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  let label;
  if (ms < 60_000) label = `${Math.max(0, Math.floor(ms / 1000))}s ago`;
  else if (ms < 3_600_000) label = `${Math.floor(ms / 60_000)} min ago`;
  else if (ms < 86_400_000) label = `${Math.floor(ms / 3_600_000)} hr ago`;
  else label = `${Math.floor(ms / 86_400_000)} d ago`;
  return <span title={new Date(iso).toLocaleString()}>{label}</span>;
}
